import {
  CreateServicePackage,
  Route,
  ServiceAction,
  ServicePackage,
  ServicePartActionUtils,
  Translator,
  UpdateServicePackage,
  populateRoute,
} from "@repo/common"
import { toast } from "@repo/ui/hooks/use-toast"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useTranslations } from "use-intl"
import { z } from "zod"
import { request } from "../api"

interface ApiErrorResponse {
  message: string
  statusCode: number
}

type ServicePackageErrorTranslator = ReturnType<
  typeof useTranslations<"alfred.lib.servicePackageErrors">
>

const ServiceActionSchema = z.nativeEnum(ServiceAction)

const translateAction = (action: string, t: Translator): string => {
  const parsed = ServiceActionSchema.safeParse(action)
  return parsed.success
    ? t(ServicePartActionUtils.toLabelKey(parsed.data))
    : action
}

const getErrorMessage = (
  error: AxiosError<ApiErrorResponse>,
  t: Translator,
  tErrors: ServicePackageErrorTranslator,
): string => {
  const message = error.response?.data?.message

  if (message?.includes("Invalid actions")) {
    // Parse: "Invalid actions [TRUE, BLEED] for part "Ekrar". Available actions: [ADJUST, CHANGE]"
    const actionsMatch = message.match(/Invalid actions \[([^\]]+)\]/)
    const partMatch = message.match(/for part "([^"]+)"/)

    if (actionsMatch && partMatch) {
      const invalidActions = actionsMatch[1]
        .split(", ")
        .map((action) => translateAction(action, t))
        .join(", ")
      const partName = partMatch[1]

      return tErrors("actionUnavailableForPart", {
        actions: invalidActions,
        part: partName,
      })
    }

    return tErrors("invalidActions")
  }

  return tErrors("generic")
}

interface ServicePackageFilter extends Partial<ServicePackage> {
  storeId: string
}

async function fetchServicePackage(id: string) {
  return await request<ServicePackage>({
    url: populateRoute(
      Route.SERVICE_JOB_PACKAGE_CONTROLLER__FIND_ONE_BY_ID,
      id,
    ),
    method: "GET",
  })
}

async function fetchServicePackages(filters: ServicePackageFilter) {
  const { storeId } = filters

  return await request<ServicePackage[]>({
    url: populateRoute(
      Route.SERVICE_JOB_PACKAGE_CONTROLLER__FIND_MANY_BY_STORE_ID,
      storeId,
    ),
    method: "GET",
  })
}

async function deleteServicePackage(id: string) {
  return await request<void>({
    url: populateRoute(Route.SERVICE_JOB_PACKAGE_CONTROLLER__DELETE, id),
    method: "DELETE",
  })
}

async function createServicePackage(
  data: CreateServicePackage,
  storeId: string,
) {
  return await request<void>({
    url: populateRoute(Route.SERVICE_JOB_PACKAGE_CONTROLLER__CREATE, storeId),
    method: "POST",
    data,
  })
}

async function updateServicePackage(id: string, data: UpdateServicePackage) {
  return await request<void>({
    url: populateRoute(Route.SERVICE_JOB_PACKAGE_CONTROLLER__UPDATE, id),
    method: "PATCH",
    data,
  })
}

async function duplicateServicePackage(id: string) {
  return await request<ServicePackage>({
    url: populateRoute(Route.SERVICE_JOB_PACKAGE_CONTROLLER__DUPLICATE, id),
    method: "POST",
  })
}

export const useServicePackageMutations = (storeId: string) => {
  const queryClient = useQueryClient()
  const t = useTranslations()
  const tErrors = useTranslations("alfred.lib.servicePackageErrors")

  const invalidateQueries = async () =>
    await queryClient.invalidateQueries(servicePackageQueries.list({ storeId }))

  const createMutation = useMutation({
    mutationFn: (data: CreateServicePackage) =>
      createServicePackage(data, storeId),
    onSettled: invalidateQueries,
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast({
        title: tErrors("createFailed"),
        description: getErrorMessage(error, t, tErrors),
        variant: "destructive",
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => {
      const promises = ids.map((id) => deleteServicePackage(id))
      return Promise.all(promises)
    },
    onSettled: invalidateQueries,
    onError: () => {
      toast({
        title: tErrors("deleteFailed"),
        description: tErrors("generic"),
        variant: "destructive",
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ data, id }: { data: UpdateServicePackage; id: string }) =>
      updateServicePackage(id, data),
    onSettled: invalidateQueries,
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast({
        title: tErrors("updateFailed"),
        description: getErrorMessage(error, t, tErrors),
        variant: "destructive",
      })
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => duplicateServicePackage(id),
    onSettled: invalidateQueries,
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast({
        title: tErrors("duplicateFailed"),
        description: getErrorMessage(error, t, tErrors),
        variant: "destructive",
      })
    },
  })

  return { createMutation, deleteMutation, updateMutation, duplicateMutation }
}

const servicePackageQueries = {
  all: () => ["service-packages"],
  lists: () => [...servicePackageQueries.all(), "list"],
  list: (filters: ServicePackageFilter) =>
    queryOptions({
      queryKey: [...servicePackageQueries.lists(), filters],
      queryFn: () => fetchServicePackages(filters),
    }),
  details: () => [...servicePackageQueries.all(), "detail"],
  detail: (id: string) =>
    queryOptions({
      queryKey: [...servicePackageQueries.details(), id],
      queryFn: () => fetchServicePackage(id),
    }),
}

export default servicePackageQueries
